import React, { type ReactNode, type ComponentProps, memo, use, useCallback, useMemo, useState } from "react";

import { Button, ComboBox, Input, type Key, Label, ListBox } from "@heroui/react";
import { useIntl } from "react-intl";

import { Contacts, GQL } from "@binale-tech/shared";
import { ContactsContext } from "@domain/context/ContactsContext";
import { useCanWrite } from "@ui-components/hooks/usePermissions";
import { Plus, Xmark } from "@gravity-ui/icons";

const HIDDEN_STYLE = { display: "none" } as const;
const handleFocusSelect = (e: React.FocusEvent<HTMLInputElement>): void => e.target.select();
const preventMouseDownDefault = (e: React.MouseEvent): void => e.preventDefault();

type TProps = Omit<ComponentProps<typeof ComboBox>, "children" | "onChange" | "value"> & {
    onChange?: (v: string) => void;
    value?: string;
    disabled?: boolean;
    placeholder?: string;
    label?: ReactNode;
    isClearable?: boolean;
};

const InputContactImpl = ({ onChange, value, disabled, placeholder, label, isClearable, ...props }: TProps) => {
    const { formatMessage } = useIntl();
    const { contacts } = use(ContactsContext);
    const [searchValue, setSearchValue] = useState("");

    const hasContactsWriteAccess = useCanWrite(GQL.IProductKey.Contacts);

    const suggestCreateContext = Boolean(!disabled && hasContactsWriteAccess && !value && searchValue.length);

    const showClear = Boolean(isClearable && !disabled && value);

    const items = useMemo(() => {
        return (contacts || [])
            .map((item: Contacts.Contact) => ({
                label: Contacts.getLabelName(item) || item.internalName,
                value: item.uuid,
                internalName: item.internalName,
            }))
            .filter((v) => {
                if (!searchValue) {
                    return true;
                }
                return (
                    v.label.toLowerCase().includes(searchValue.toLowerCase()) ||
                    v.internalName.toLowerCase().includes(searchValue.toLowerCase())
                );
            });
    }, [contacts, searchValue]);

    const handleSelectionChange = useCallback(
        (key: Key) => {
            onChange?.(key ? String(key) : undefined);
        },
        [onChange],
    );

    const handleOpenContact = useCallback(() => window.open(`/contacts?modal=${searchValue}`, "_blank"), [searchValue]);
    const handleClear = useCallback(() => onChange?.(undefined), [onChange]);

    return (
        <div className="relative">
            <ComboBox
                aria-label={formatMessage({ id: "app.accounting.selectContact" })}
                {...props}
                isDisabled={disabled}
                items={items}
                menuTrigger="input"
                selectedKey={value || null}
                onSelectionChange={handleSelectionChange}
                onInputChange={setSearchValue}
                onFocus={handleFocusSelect}
                allowsCustomValue={false}
            >
                {label && <Label>{label}</Label>}
                <ComboBox.InputGroup>
                    <Input
                        className={suggestCreateContext ? "pr-9" : showClear ? "pr-16" : undefined}
                        placeholder={placeholder}
                    />
                    {/*
                     * Keep the dropdown trigger permanently mounted and only hide it via inline
                     * `display` while the add-contact button takes its place. ComboBox builds its
                     * collection by re-rendering its children into a hidden <template> element
                     * (react-aria `Hidden`); nodes inside a <template> live in the template-contents
                     * document whose `ownerDocument.defaultView` is null. The old code mounted a
                     * Tooltip-wrapped button here on `suggestCreateContext`, so react-aria walked one
                     * of those nodes during the hidden pass and crashed with
                     * "Cannot read properties of null (reading 'HTMLElement')". The add-contact button
                     * now lives OUTSIDE the ComboBox (below) so it never enters that pass.
                     */}
                    <ComboBox.Trigger style={suggestCreateContext ? HIDDEN_STYLE : undefined} />
                </ComboBox.InputGroup>
                <ComboBox.Popover className="min-w-sm">
                    <ListBox items={items}>
                        {(item) => (
                            <ListBox.Item key={item.value} id={item.value} textValue={item.label || item.internalName}>
                                <div className="flex flex-col gap-1 py-1">
                                    <span className="text-xs text-muted leading-tight truncate">
                                        {item.internalName}
                                    </span>
                                    <span className="text-sm font-medium leading-tight truncate">{item.label}</span>
                                </div>
                            </ListBox.Item>
                        )}
                    </ListBox>
                </ComboBox.Popover>
            </ComboBox>
            {/*
             * Rendered OUTSIDE the ComboBox (absolutely positioned over the trigger slot) so it never
             * enters the ComboBox's hidden <template> collection pass - that pass is what crashed
             * react-aria when a focusable lived inside ComboBox.InputGroup. The hint uses a native
             * `title` rather than a HeroUI Tooltip to avoid re-introducing overlay/focusable machinery.
             */}
            {suggestCreateContext && (
                <Button
                    isIconOnly
                    size="sm"
                    excludeFromTabOrder
                    aria-label={formatMessage({ id: "app.accounting.addContactWithName" })}
                    className="absolute end-1 top-1/2 z-10 -translate-y-1/2"
                    onMouseDown={preventMouseDownDefault}
                    onPress={handleOpenContact}
                >
                    <Plus className="size-4" />
                </Button>
            )}
            {showClear && (
                <Button
                    isIconOnly
                    size="sm"
                    excludeFromTabOrder
                    aria-label={formatMessage({ id: "app.button.reset" })}
                    className="absolute end-8 top-1/2 z-10 -translate-y-1/2"
                    onMouseDown={preventMouseDownDefault}
                    onPress={handleClear}
                >
                    <Xmark className="size-4" />
                </Button>
            )}
        </div>
    );
};

export const InputContact = memo(InputContactImpl);
